import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

// eslint-disable-next-line max-len
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { AppState } from '@next/arable-lib/advice';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';

import { Subject, take, takeUntil } from 'rxjs';

import { BemCropFieldSortingService } from 'src/app/shared/services/bem-crop-field-sorting.service';

import { DatePipe, DecimalPipe, LowerCasePipe, NgClass, TitleCasePipe } from '@angular/common';
import { EmptyWidgetComponent } from '../../shared/components/empty-widget.component';
import { BemAIIdentifiers, eLearningUrl } from '../shared/constants';
import { CropFieldNutrientCalculations, Nutrient, NutrientCalculations, NutrientKey, SampleTakingMoment } from '../shared/models/models';
import { loadBFPData } from '../store/bfp.actions';
import { selectCropFieldNutrientCalculation } from '../store/bfp.feature';
import { selectCropYear } from '../../store/selectors';

@Component({
  selector: 'app-bfp-dashboard-widget',
  templateUrl: './bfp-dashboard-widget.component.html',
  styleUrl: './bfp-dashboard-widget.component.scss',
  imports: [
    SummaryCardComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    EmptyWidgetComponent,
    LowerCasePipe,
    DecimalPipe,
    TitleCasePipe,
    DatePipe,
    TranslatePipe,
  ],
})

export class BfpDashboardWidgetComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly localeService = inject(LocaleService);
  private readonly fieldSortingService = inject(BemCropFieldSortingService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  displayedColumns: string[] = ['perceel', 'bemonstring', 'nutrient1', 'nutrient2'];
  locale!: string;

  actions = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => this.openInfo(),
    },
  ] satisfies SummaryCardAction[];

  private readonly cropFieldsSignal = toSignal(
    this.store.select(selectCropFieldNutrientCalculation),
    { initialValue: null },
  );

  readonly sortedFields = computed(() => {
    const fields = this.cropFieldsSignal();
    return fields ? this.fieldSortingService.sortFields(fields) : [];
  });

  readonly dataSource = computed(() => {
    const source = new MatTableDataSource<CropFieldNutrientCalculations>();
    source.data = this.sortedFields();
    return source;
  });

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  ngOnInit(): void {
    this.store
      .select(selectCropYear)
      .pipe(take(1))
      .subscribe((cropYear) => this.store.dispatch(loadBFPData({ year: cropYear })));
  }

  openCropfieldInfo(row: CropFieldNutrientCalculations) {
    this.ai.trackEvent(BemAIIdentifiers.clickTable, {});
    this.router.navigate([`/bem/${row.id}`]);
  }

  getNutrientKeys(field: CropFieldNutrientCalculations): string[] {
    return Object.keys(field.nutrientCalculation).filter((k) => field.nutrientCalculation[k as keyof typeof field.nutrientCalculation]?.adviceTxt);
  }

  getColorFromAdvice(nutrient: Nutrient | undefined, nutrientKey: NutrientKey): string | undefined {
    return this.fieldSortingService.getColorFromAdvice(nutrient, nutrientKey);
  }

  getColorFromRecKgha(nutrient: Nutrient | undefined): string | undefined {
    return this.fieldSortingService.getColorFromRecKgha(nutrient);
  }

  getNutrientColor(nutrientCalculations: NutrientCalculations, n: SampleTakingMoment): void {
    this.fieldSortingService.getNutrientColor(nutrientCalculations, n);
  }

  sortFields(fields: CropFieldNutrientCalculations[]): CropFieldNutrientCalculations[] {
    return this.fieldSortingService.sortFields(fields);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private openInfo() {
    this.ai.trackEvent(BemAIIdentifiers.clickInfoMyArable, {});
    window.open(eLearningUrl);
  }
}
